import React, { useEffect, useState } from "react";
import { useRealtime } from "../contexts/RealtimeContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FileText,
  Plus,
  ChevronRight,
  Trash2,
  Edit2,
  Sparkles,
  Files,
  Activity,
  Search,
} from "lucide-react";
import LoadingSkeleton from "../components/LoadingSkeleton";
import LoadingButton from "../components/LoadingButton";
import ScannerForm from "../components/ScannerForm";
import ScannerAssistant from "../components/ScannerAssistant";
import { motion, AnimatePresence } from "framer-motion";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

export default function ScannersList() {
  const { user } = useAuth();
  const [scanners, setScanners] = useState([]);
  // «Otros documentos» (auto_fallback) la crea el sistema para lo que no encaja en ninguna plantilla: no cuenta para el tope.
  const countedScanners = scanners.filter((s) => !s?.auto_fallback).length;
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [editingScanner, setEditingScanner] = useState(null);
  const [showCreationChoice, setShowCreationChoice] = useState(false);
  const [useAssistant, setUseAssistant] = useState(false);
  const [assistantGeneratedData, setAssistantGeneratedData] = useState(null);
  const [assistantMessages, setAssistantMessages] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const saasNumber = process.env.REACT_APP_SAAS_NUMBER;
  const { lastEvent } = useRealtime();

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api
      .listScanners()
      .then((r) => setScanners(r))
      .finally(() => setLoading(false));
  }, [user]);

  // Fetch recent history count for each scanner (best-effort)
  useEffect(() => {
    // Only run when the number of scanners changes and we don't already
    // have history counts for them. Using scanners.length prevents
    // re-running this effect when we update scanner objects with
    // historyCount/recentHistory which would otherwise cause an
    // infinite loop of fetching.
    if (!scanners || scanners.length === 0) return;
    if (scanners.every((s) => typeof s.historyCount === "number")) return;

    const fetchCounts = async () => {
      try {
        const promises = scanners.map(async (s) => {
          try {
            const res = await api.getScannerHistory(s.id, { limit: 5 });
            let count = res?.count;
            let recentItems = res?.items || [];
            if (typeof count !== "number" && res?.nextCursor) {
              try {
                const resAll = await api.getScannerHistory(s.id, {
                  limit: 10000,
                });
                count = resAll?.count ?? (resAll?.items || []).length;
              } catch (err) {
                count = recentItems.length;
              }
            }
            count = typeof count === "number" ? count : recentItems.length;
            return {
              id: s.id,
              historyCount: count,
              recent: recentItems,
            };
          } catch (err) {
            return { id: s.id, historyCount: 0, recent: [] };
          }
        });
        const res = await Promise.all(promises);
        setScanners((prev) => {
          return prev.map((s) => {
            const found = res.find((r) => r.id === s.id) || {};
            return {
              ...s,
              historyCount:
                typeof found.historyCount === "number"
                  ? found.historyCount
                  : (s.history || []).length || 0,
              recentHistory: found.recent || s.history || [],
            };
          });
        });
      } catch (err) {
        // ignore
      }
    };
    fetchCounts();
  }, [scanners.length]);

  // On realtime event, refresh a single scanner's history count
  useEffect(() => {
    if (!lastEvent || !lastEvent.scannerId) return;
    const scannerId = lastEvent.scannerId;
    api
      .getScannerHistory(scannerId, { limit: 5 })
      .then((res) => {
        setScanners((prev) =>
          prev.map((s) =>
            s.id === scannerId
              ? {
                  ...s,
                  historyCount: res?.count || (res?.items || []).length || 0,
                  recentHistory: res?.items || [],
                }
              : s
          )
        );
      })
      .catch(() => {});
  }, [lastEvent]);

  const refresh = () => {
    return api.listScanners().then(setScanners);
  };

  const handleSave = async (payload) => {
    setCreating(true);
    try {
      if (editingScanner && editingScanner.id) {
        await api.updateScanner(editingScanner.id, payload);
        setEditingScanner(null);
        refresh();
      } else {
        const response = await api.createScanner(payload);
        const scannerId =
          response?.id ||
          response?.scanner?.id ||
          response?.data?.id ||
          response?.data?.scanner?.id;
        if (scannerId) {
          navigate(`/scanners/${scannerId}`);
        } else {
          refresh();
        }
      }
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (scanner) => {
    setEditingScanner(scanner);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingScanner(null);
    setCreating(false);
    setShowCreationChoice(false);
    setUseAssistant(false);
    setAssistantMessages([]);
    setAssistantGeneratedData(null);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation(); // Prevent card click
    if (!window.confirm(t("scanners.delete_confirm"))) return;
    setDeleting(id);
    try {
      await api.deleteScanner(id);
      refresh();
    } finally {
      setDeleting(null);
    }
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 max-w-7xl mx-auto space-y-8"
    >
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <motion.h2
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 mb-2"
          >
            {t("scanners.title")}
          </motion.h2>
          <motion.p
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-gray-500 font-medium"
          >
            {t(
              "scanners.subtitle",
              "Manage your document extraction workflows"
            )}
          </motion.p>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center bg-white/60 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/50 shadow-sm text-gray-600 text-sm font-medium">
            <Activity className="w-4 h-4 mr-2 text-primary-500" />
            {scanners.length} {t("scanners.scanners_count")}
          </div>
          {countedScanners <
          (user?.max_scanners === null || user?.max_scanners === -1
            ? Infinity
            : user?.max_scanners || 10) ? (
            <button
              type="button"
              onClick={() => {
                setEditingScanner(null);
                setShowCreationChoice(true);
                setAssistantMessages([]);
                setAssistantGeneratedData(null);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:shadow-primary-500/20 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <Plus className="w-5 h-5" />
              {t("scanners.new")}
            </button>
          ) : (
            <div className="flex flex-col items-end">
              <button
                disabled
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-400 rounded-xl font-semibold cursor-not-allowed"
              >
                <Plus className="w-5 h-5" />
                {t("scanners.new")}
              </button>
              <span className="text-[10px] text-amber-600 font-medium mt-1">
                {t("scanners.limit_reached", {
                  limit:
                    user?.max_scanners === -1 || user?.max_scanners === null
                      ? "∞"
                      : user?.max_scanners || 10,
                }) ||
                  `Límite de ${
                    user?.max_scanners === -1 || user?.max_scanners === null
                      ? "∞"
                      : user?.max_scanners || 10
                  } escáneres alcanzado`}
              </span>
              <Link
                to="/pricing"
                className="text-[10px] text-primary-600 hover:text-primary-700 font-bold underline transition-colors mt-1"
              >
                {t("scanners.upgrade_for_more") ||
                  "Mejora tu plan para scanner ilimitados"}
              </Link>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {(editingScanner || showCreationChoice || creating || useAssistant) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="relative overflow-hidden"
          >
            <div className="bg-white/80 backdrop-blur-xl p-8 rounded-3xl shadow-xl border border-white/60 relative z-10">
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30">
                  {editingScanner?.id ? (
                    <Edit2 className="w-7 h-7" />
                  ) : useAssistant ? (
                    <Sparkles className="w-7 h-7" />
                  ) : (
                    <FileText className="w-7 h-7" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-2xl text-gray-900">
                    {editingScanner && editingScanner.id
                      ? t("scanners.edit_scanner_section") || "Edit Scanner"
                      : useAssistant
                      ? t("scanners.create_with_assistant") ||
                        "Create with AI Assistant"
                      : t("scanners.create_manually") || "Create Manually"}
                  </h3>
                  <p className="text-gray-500 font-medium mt-1">
                    {useAssistant
                      ? t(
                          "scanners.chat_with_ai",
                          "Chat with AI to build your scanner"
                        )
                      : t(
                          "scanners.configure_directly",
                          "Configure extraction fields directly"
                        )}
                  </p>
                </div>

                <button
                  onClick={handleCancelEdit}
                  className="ml-auto p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/50 rounded-xl transition-colors"
                >
                  <span className="sr-only">{t("common.close")}</span>
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {showCreationChoice ? (
                // Pantalla de selección entre manual o asistente
                <div className="max-w-4xl mx-auto py-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Opción Manual */}
                    <motion.button
                      whileHover={{
                        scale: 1.02,
                        backgroundColor: "rgba(255,255,255,0.6)",
                      }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowCreationChoice(false);
                        setUseAssistant(false);
                        setEditingScanner({});
                      }}
                      className="relative p-8 rounded-3xl border-2 border-gray-100 bg-white/50 text-left transition-all duration-300 hover:border-primary-200 hover:shadow-xl group overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 p-32 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full opacity-0 group-hover:opacity-50 transition-all duration-500 blur-3xl -mr-16 -mt-16 pointer-events-none" />

                      <div className="relative z-10 w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
                        <FileText className="w-8 h-8 text-gray-600 group-hover:text-primary-600 transition-colors" />
                      </div>

                      <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-700 transition-colors">
                        {t("scanners.manual_creation") || "Manual Creation"}
                      </h4>
                      <p className="text-gray-500 mb-6 leading-relaxed">
                        {t("scanners.manual_details") ||
                          "Perfect for users who know exactly what fields they need and want full control over the scanner configuration."}
                      </p>

                      <span className="inline-flex items-center text-sm font-bold text-gray-400 group-hover:text-primary-600 transition-colors">
                        {t(
                          "scanners.start_configuration",
                          "Start configuration"
                        )}{" "}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </span>
                    </motion.button>

                    {/* Opción Asistente IA */}
                    <motion.button
                      whileHover={{
                        scale: 1.02,
                        backgroundColor: "rgba(255,255,255,0.8)",
                      }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowCreationChoice(false);
                        setUseAssistant(true);
                      }}
                      className="relative p-8 rounded-3xl border-2 border-primary-100 bg-gradient-to-br from-white to-primary-50/30 text-left transition-all duration-300 hover:border-primary-300 hover:shadow-xl group overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary-100/40 to-purple-100/40 rounded-full blur-3xl -mr-16 -mt-16 animate-pulse" />

                      <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-primary-500/20">
                        <Sparkles className="w-8 h-8 text-white" />
                      </div>

                      <h4 className="text-xl font-bold text-gray-900 mb-2">
                        {t("scanners.ai_assistant") || "AI Assistant"}
                      </h4>
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {t("scanners.ai_details") ||
                          "Let our AI assistant guide you through the process of creating your scanner by asking you simple questions."}
                      </p>

                      <span className="inline-flex items-center text-sm font-bold text-primary-600">
                        {t("scanners.start_with_ai", "Start with AI")}{" "}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </span>
                    </motion.button>
                  </div>
                </div>
              ) : useAssistant ? (
                // Asistente IA
                <ScannerAssistant
                  onSave={handleSave}
                  creating={creating}
                  showManualOption={true}
                  showActionButtons={true}
                  initialMessages={assistantMessages}
                  onMessagesChange={setAssistantMessages}
                  onSwitchToManual={() => {
                    setUseAssistant(false);
                    setShowCreationChoice(false);
                    setEditingScanner(assistantGeneratedData || {});
                  }}
                  onSwitchToManualFromForm={(data) => {
                    setUseAssistant(false);
                    setShowCreationChoice(false);
                    setEditingScanner(data || {});
                  }}
                  onScannerGenerated={(data) => setAssistantGeneratedData(data)}
                />
              ) : (
                // Creación manual
                <>
                  {/* {saasNumber && (
                    <div className="mb-6 text-sm text-primary-700 bg-primary-50 px-4 py-3 rounded-xl border border-primary-200 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <activity className="w-4 h-4 text-primary-600" />
                      </div>
                      <span>
                        Documents will be sent to:{" "}
                        <strong className="font-bold">{saasNumber}</strong>
                      </span>
                    </div>
                  )} */}
                  <ScannerForm
                    onSave={handleSave}
                    initial={editingScanner || {}}
                    creating={creating}
                    canCreate={!!user}
                    isEditing={!!editingScanner && !!editingScanner.id}
                    onCancel={handleCancelEdit}
                  />
                  {(!editingScanner || !editingScanner.id) && (
                    <div className="flex justify-center pt-8 border-t border-gray-100 mt-8">
                      <button
                        onClick={() => {
                          setUseAssistant(true);
                          setCreating(false);
                        }}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-50 text-primary-600 hover:bg-primary-100 hover:text-primary-700 font-semibold transition-all text-sm group"
                      >
                        <Sparkles className="w-4 h-4 transition-transform group-hover:rotate-12" />
                        {editingScanner
                          ? t("scanners.adjust_with_assistant") ||
                            "Adjust with assistant"
                          : t("scanners.create_with_assistant") ||
                            "Create with AI Assistant"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-6">
        {loading ? (
          <LoadingSkeleton variant="list" />
        ) : scanners.length === 0 ? (
          <div className="text-center py-24 bg-white/50 backdrop-blur-lg rounded-3xl border border-white/60 border-dashed border-gray-300">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t("scanners.no_scanners")}
            </h3>
            <p className="text-gray-500 max-w-sm mx-auto mb-8">
              {t("uiFixes.empty_scanners")}
            </p>
            {countedScanners <
            (user?.max_scanners === null || user?.max_scanners === -1
              ? Infinity
              : user?.max_scanners || 10) ? (
              <button
                onClick={() => setShowCreationChoice(true)}
                className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-primary-700 transition-all"
              >
                {t("uiFixes.create_scanner")}
              </button>
            ) : (
              <div className="flex flex-col items-center">
                <button
                  disabled
                  className="px-6 py-3 bg-gray-200 text-gray-400 rounded-xl font-semibold cursor-not-allowed"
                >
                  {t("uiFixes.create_scanner")}
                </button>
                <p className="text-sm text-amber-600 font-medium mt-3">
                  {t("scanners.limit_reached", {
                    limit:
                      user?.max_scanners === -1 || user?.max_scanners === null
                        ? "∞"
                        : user?.max_scanners || 10,
                  }) ||
                    `Has alcanzado el límite de ${
                      user?.max_scanners === -1 || user?.max_scanners === null
                        ? "∞"
                        : user?.max_scanners || 10
                    } escáneres para tu plan.`}
                </p>
                <Link
                  to="/pricing"
                  className="text-sm text-primary-600 hover:text-primary-700 font-bold underline transition-colors mt-2"
                >
                  {t("scanners.upgrade_for_more") ||
                    "Mejora tu plan para scanner ilimitados"}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 gap-4"
          >
            {scanners.map((s) => (
              <motion.div
                variants={item}
                key={s.id}
                onClick={() => navigate(`/scanners/${s.id}`)}
                className={`group relative p-6 bg-white/70 backdrop-blur-xl rounded-2xl border transition-all duration-300 cursor-pointer ${
                  editingScanner?.id === s.id
                    ? "border-primary-500 shadow-[0_0_0_2px_rgba(var(--primary-rgb),0.2)]"
                    : "border-white/50 shadow-sm hover:shadow-xl hover:shadow-gray-200/50 hover:bg-white/90 hover:border-primary-100 hover:-translate-y-0.5"
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-start gap-5">
                    <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-50 to-white border border-primary-100 text-primary-600 shadow-sm group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
                      <FileText className="w-7 h-7" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-gray-900 group-hover:text-primary-600 transition-colors mb-2">
                        {s.name}
                      </h4>
                      <p className="text-sm text-gray-500 line-clamp-2 max-w-lg mb-4">
                        {s.description}
                      </p>

                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center text-xs font-semibold text-gray-500 bg-gray-50/80 px-2.5 py-1 rounded-lg border border-gray-100">
                          <Files className="w-3 h-3 mr-1.5" />
                          {t("scanners.fields_count", {
                            count: (s.fields || []).length,
                          })}
                        </div>
                        <div className="flex items-center text-xs font-semibold text-gray-500 bg-gray-50/80 px-2.5 py-1 rounded-lg border border-gray-100">
                          <Activity className="w-3 h-3 mr-1.5" />
                          {t("scanners.uploads_count", {
                            count:
                              (s.historyCount ?? (s.history || []).length) || 0,
                          })}
                        </div>
                        {/* {saasNumber && (
                          <div className="flex items-center text-xs font-semibold text-primary-700 bg-primary-50/50 px-2.5 py-1 rounded-lg border border-primary-100">
                            {t("scanners.to", "To:")} {saasNumber}
                          </div>
                        )} */}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start md:self-auto border-t md:border-0 border-gray-100 pt-4 md:pt-0 w-full md:w-auto">
                    <button
                      className="p-2.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(s);
                      }}
                      title={t("scanners.edit_scanner_title") || "Edit Scanner"}
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>

                    <LoadingButton
                      loading={deleting === s.id}
                      className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                      onClick={(e) => handleDelete(s.id, e)}
                      title={t("scanners.delete_scanner_title")}
                    >
                      <Trash2 className="w-5 h-5" />
                    </LoadingButton>

                    <div className="md:ml-2">
                      <button
                        className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all transform active:scale-95 text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/scanners/${s.id}`);
                        }}
                      >
                        {t("scanners.open_button")}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </motion.main>
  );
}
